/**
 * PortalPage.js — GoHost Staff Portal (full-screen authenticated view)
 *
 * Route: /portal
 *
 * Reads session from localStorage (written by AccountModal on login).
 * If no session found, redirects to the business site home.
 * Refresh-safe: localStorage persists across page reloads.
 *
 * Role-based navigation:
 *   owner    → Schedule, Employees, Payroll, Analytics, Reports, Settings
 *   manager  → Schedule, Employees, Payroll, Reservations, Reports
 *   employee → My Schedule, My Pay
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Logo from '../assets/img/header/gohost_header_logo1.png';
import { PORTAL_SESSION_KEY, SESSION_EXPIRED_KEY } from './AccountModal';
import ScheduleView from './schedule/ScheduleView';
import EmployeeScheduleView from './schedule/EmployeeScheduleView';
import EmployeeDirectory from './portal/EmployeeDirectory';
import MyPay from './portal/MyPay';
import AccountSettings from './portal/AccountSettings';
import CommissionSettings from './portal/CommissionSettings';
import MenusView from './portal/MenusView';
import SettingsView from './portal/SettingsView';
import PayrollView from './portal/PayrollView';
import HoursView from './portal/HoursView';
import ReportsView from './portal/ReportsView';
import InventoryView from './portal/InventoryView';
import TimesheetsView from './portal/TimesheetsView';
import TablesView from './portal/TablesView';
import AnalyticsView from './portal/AnalyticsView';
import WebsiteView from './portal/WebsiteView';
import DevicesView from './portal/DevicesView';
import TipDistributionView from './portal/TipDistributionView';
import UnsavedChangesModal from '../utilities/UnsavedChangesModal';

// ─── Feather icon primitives ─────────────────────────────────────────────────
// All icons share: viewBox 0 0 24 24, fill none, stroke currentColor, sw 2

const ICON_PATHS = {
  grid:         <><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/></>,
  calendar:     <><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></>,
  users:        <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
  dollarSign:   <><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></>,
  clock:        <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>,
  percent:      <><line x1="19" y1="5" x2="5" y2="19"/><circle cx="6.5" cy="6.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/></>,
  bookOpen:     <><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></>,
  package:      <><line x1="16.5" y1="9.4" x2="7.5" y2="4.21"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></>,
  watch:        <><circle cx="12" cy="12" r="7"/><polyline points="12 9 12 12 13.5 13.5"/><path d="M16.51 17.35l-.35 3.83a2 2 0 0 1-2 1.82H9.83a2 2 0 0 1-2-1.82l-.35-3.83m.01-10.7l.35-3.83A2 2 0 0 1 9.83 1h4.35a2 2 0 0 1 2 1.82l.35 3.83"/></>,
  barChart2:    <><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></>,
  fileText:     <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></>,
  settings:     <><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></>,
  mousePointer: <><path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z"/><path d="M13 13l6 6"/></>,
  tablet:       <><rect x="4" y="2" width="16" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18" strokeWidth="3"/></>,
  user:         <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></>,
  bookmark:     <><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></>,
};

const FIcon = ({ name, size = 15 }) => (
  <svg
    width={size} height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{ display: 'inline-block', flexShrink: 0 }}
  >
    {ICON_PATHS[name]}
  </svg>
);

// ─── Tab metadata: icon + color theme ────────────────────────────────────────
// activeCls  — background + text when selected
// inactiveCls — text + hover when not selected
// headerCls  — color for the page heading text + icon
// dotCls     — accent dot color for future use

const TAB_META = {
  'tables':       { icon: 'grid',         activeCls: 'bg-teal-600 text-white',        inactiveCls: 'text-teal-700 hover:bg-teal-50',         headerCls: 'text-teal-700'    },
  'schedule':     { icon: 'calendar',     activeCls: 'bg-indigo-600 text-white',      inactiveCls: 'text-indigo-700 hover:bg-indigo-50',     headerCls: 'text-indigo-700'  },
  'my-schedule':  { icon: 'calendar',     activeCls: 'bg-indigo-600 text-white',      inactiveCls: 'text-indigo-700 hover:bg-indigo-50',     headerCls: 'text-indigo-700'  },
  'employees':    { icon: 'users',        activeCls: 'bg-purple-600 text-white',      inactiveCls: 'text-purple-700 hover:bg-purple-50',     headerCls: 'text-purple-700'  },
  'payroll':      { icon: 'dollarSign',   activeCls: 'bg-emerald-600 text-white',     inactiveCls: 'text-emerald-700 hover:bg-emerald-50',   headerCls: 'text-emerald-700' },
  'my-pay':       { icon: 'dollarSign',   activeCls: 'bg-emerald-600 text-white',     inactiveCls: 'text-emerald-700 hover:bg-emerald-50',   headerCls: 'text-emerald-700' },
  'timesheets':   { icon: 'clock',        activeCls: 'bg-amber-500 text-white',       inactiveCls: 'text-amber-600 hover:bg-amber-50',       headerCls: 'text-amber-600'   },
  'tip-distribution': { icon: 'dollarSign', activeCls: 'bg-amber-600 text-white',     inactiveCls: 'text-amber-600 hover:bg-amber-50',       headerCls: 'text-amber-600'   },
  'commission':   { icon: 'percent',      activeCls: 'bg-yellow-500 text-white',      inactiveCls: 'text-yellow-600 hover:bg-yellow-50',     headerCls: 'text-yellow-600'  },
  'menus':        { icon: 'bookOpen',     activeCls: 'bg-orange-500 text-white',      inactiveCls: 'text-orange-600 hover:bg-orange-50',     headerCls: 'text-orange-600'  },
  'inventory':    { icon: 'package',      activeCls: 'bg-stone-600 text-white',       inactiveCls: 'text-stone-600 hover:bg-stone-100',      headerCls: 'text-stone-600'   },
  'hours':        { icon: 'watch',        activeCls: 'bg-sky-600 text-white',         inactiveCls: 'text-sky-700 hover:bg-sky-50',           headerCls: 'text-sky-700'     },
  'analytics':    { icon: 'barChart2',    activeCls: 'bg-blue-600 text-white',        inactiveCls: 'text-blue-700 hover:bg-blue-50',         headerCls: 'text-blue-700'    },
  'reports':      { icon: 'fileText',     activeCls: 'bg-violet-600 text-white',      inactiveCls: 'text-violet-700 hover:bg-violet-50',     headerCls: 'text-violet-700'  },
  'settings':     { icon: 'settings',     activeCls: 'bg-zinc-700 text-white',        inactiveCls: 'text-zinc-600 hover:bg-zinc-100',        headerCls: 'text-zinc-700'    },
  'website':      { icon: 'mousePointer', activeCls: 'bg-pink-600 text-white',        inactiveCls: 'text-pink-600 hover:bg-pink-50',         headerCls: 'text-pink-600'    },
  'devices':      { icon: 'tablet',       activeCls: 'bg-cyan-600 text-white',        inactiveCls: 'text-cyan-700 hover:bg-cyan-50',         headerCls: 'text-cyan-700'    },
  'profile':      { icon: 'user',         activeCls: 'bg-rose-500 text-white',        inactiveCls: 'text-rose-600 hover:bg-rose-50',         headerCls: 'text-rose-600'    },
  'reservations':   { icon: 'bookmark',     activeCls: 'bg-sky-600 text-white',         inactiveCls: 'text-sky-700 hover:bg-sky-50',           headerCls: 'text-sky-700'     },
};

const DEFAULT_META = { icon: 'grid', activeCls: 'bg-neutral-900 text-white', inactiveCls: 'text-neutral-600 hover:bg-neutral-100', headerCls: 'text-neutral-800' };

// ─── Nav config per role ─────────────────────────────────────────────────────

const buildNav = (user) => {
  const p = user.permissions || {};
  const role = user.role;

  if (role === 'owner') {
    return [
      { id: 'tables',         label: 'Tables'         },
      { id: 'schedule',       label: 'Schedule'       },
      { id: 'employees',   label: 'Employees'   },
      { id: 'payroll',     label: 'Payroll'     },
      { id: 'timesheets',      label: 'Timesheets'      },
      { id: 'tip-distribution', label: 'Tips'            },
      { id: 'commission',      label: 'Commission'      },
      { id: 'menus',       label: 'Menus'       },
      { id: 'inventory',   label: 'Inventory'   },
      { id: 'hours',       label: 'Hours'       },
      { id: 'analytics',   label: 'Analytics'   },
      { id: 'reports',     label: 'Reports'     },
      { id: 'settings',    label: 'Settings'    },
      { id: 'website',     label: 'Website'     },
      { id: 'devices',     label: 'Devices'     },
      { id: 'profile',     label: 'My Profile'  },
    ];
  }

  if (role === 'manager') {
    return [
      { id: 'tables',       label: 'Tables'       },
      { id: 'schedule',     label: 'Schedule'     },
      ...(p.manageEmployees || p.canAddEmployees || p.canTerminateEmployees ? [{ id: 'employees', label: 'Employees' }] : []),
      ...(p.viewPayroll     ? [{ id: 'payroll',   label: 'Payroll'    }] : []),
      { id: 'timesheets',   label: 'Timesheets'   },
      ...(p.manageReservations ? [{ id: 'reservations', label: 'Reservations' }] : []),
      ...(p.manageMenu      ? [{ id: 'menus',     label: 'Menus'      }] : []),
      ...(p.manageHours     ? [{ id: 'hours',     label: 'Hours'      }] : []),
      ...(p.viewAnalytics   ? [{ id: 'analytics', label: 'Analytics'  }] : []),
      ...(p.viewReports     ? [{ id: 'reports',   label: 'Reports'    }] : []),
      { id: 'profile',      label: 'My Profile'   },
    ];
  }

  return [
    { id: 'my-schedule', label: 'My Schedule' },
    { id: 'my-pay',      label: 'My Pay'      },
    ...(p.manageHours ? [{ id: 'hours', label: 'Hours' }] : []),
    { id: 'profile',     label: 'My Profile'  },
  ];
};

// ─── Role badge ──────────────────────────────────────────────────────────────

const ROLE_BADGE = {
  owner:    'bg-amber-100 text-amber-800 border border-amber-200',
  manager:  'bg-blue-100 text-blue-700 border border-blue-200',
  employee: 'bg-neutral-100 text-neutral-600 border border-neutral-200',
};

// ─── Placeholder panel ───────────────────────────────────────────────────────

const PlaceholderPanel = ({ title, description }) => (
  <div className="flex flex-col items-center justify-center h-64 border-2 border-dashed border-neutral-200 rounded-xl text-center px-8">
    <p className="text-lg font-semibold text-neutral-400">{title}</p>
    {description && (
      <p className="text-sm text-neutral-300 mt-2 max-w-xs">{description}</p>
    )}
  </div>
);

// ─── Panel content ───────────────────────────────────────────────────────────

const PANELS = {
  'tables': (user) => (
    <TablesView user={user} restaurantId={user.primaryRestaurantId} />
  ),

  'schedule': (user) => (
    <ScheduleView user={user} restaurantId={user.primaryRestaurantId} />
  ),
  'my-schedule': (user) => (
    <EmployeeScheduleView user={user} restaurantId={user.primaryRestaurantId} />
  ),

  'my-pay': (user) => (
    <MyPay user={user} restaurantId={user.primaryRestaurantId} />
  ),

  'profile': (user, onDirtyChange) => (
    <AccountSettings user={user} onDirtyChange={onDirtyChange} />
  ),

  'employees': (user) => (
    <EmployeeDirectory user={user} restaurantId={user.primaryRestaurantId} />
  ),

  'payroll': (user) => (
    <PayrollView user={user} restaurantId={user.primaryRestaurantId} />
  ),

  'timesheets': (user) => (
    <TimesheetsView user={user} restaurantId={user.primaryRestaurantId} />
  ),
  'analytics': (user) => (
    <AnalyticsView restaurantId={user.primaryRestaurantId} />
  ),

  'reports': (user) => (
    <ReportsView user={user} restaurantId={user.primaryRestaurantId} />
  ),

  'reservations': () => (
    <PlaceholderPanel
      title="Reservations"
      description="Override, cancel, or reschedule any reservation."
    />
  ),


  'tip-distribution': (user) => (
    <TipDistributionView restaurantId={user.primaryRestaurantId} />
  ),

  'commission': (user, onDirtyChange) => (
    <CommissionSettings restaurantId={user.primaryRestaurantId} onDirtyChange={onDirtyChange} />
  ),

  'menus': (user) => (
    <MenusView restaurantId={user.primaryRestaurantId} />
  ),

  'inventory': (user) => (
    <InventoryView user={user} restaurantId={user.primaryRestaurantId} />
  ),

  'settings': (user) => (
    <SettingsView restaurantId={user.primaryRestaurantId} user={user} />
  ),

  'website': (user) => (
    <WebsiteView restaurantId={user.primaryRestaurantId} />
  ),

  'devices': (user) => (
    <DevicesView restaurantId={user.primaryRestaurantId} />
  ),

  'hours': (user, onDirtyChange) => (
    <HoursView restaurantId={user.primaryRestaurantId} onDirtyChange={onDirtyChange} />
  ),
};

// ─── Portal page ─────────────────────────────────────────────────────────────

const PortalPage = () => {
  const [user, setUser]           = useState(null);
  const [activeTab, setActiveTab] = useState(null);
  const [pendingTabId, setPendingTabId] = useState(null);
  const [guardSaving, setGuardSaving]   = useState(false);
  const saveFnRef = useRef(null);

  const handleDirtyChange = useCallback((isDirty, saveFn = null) => {
    saveFnRef.current = isDirty ? saveFn : null;
  }, []);

  const handleTabClick = useCallback((tabId) => {
    if (saveFnRef.current) {
      setPendingTabId(tabId);
    } else {
      setActiveTab(tabId);
    }
  }, []);

  const handleGuardSave = async () => {
    setGuardSaving(true);
    try {
      await saveFnRef.current?.();
      saveFnRef.current = null;
      setActiveTab(pendingTabId);
      setPendingTabId(null);
    } catch (_) {}
    finally { setGuardSaving(false); }
  };

  const handleGuardLeave = () => {
    saveFnRef.current = null;
    setActiveTab(pendingTabId);
    setPendingTabId(null);
  };

  const handleGuardStay = () => setPendingTabId(null);

  useEffect(() => {
    // Check localStorage first (Remember me), then sessionStorage (session-only login)
    const raw = localStorage.getItem(PORTAL_SESSION_KEY) || sessionStorage.getItem(PORTAL_SESSION_KEY);
    if (!raw) { window.location.href = '/'; return; }
    try {
      const parsed = JSON.parse(raw);
      setUser(parsed);
      const tabs = buildNav(parsed);
      const urlTab = new URLSearchParams(window.location.search).get('tab');
      const matchedTab = urlTab && tabs.find(t => t.id === urlTab);
      setActiveTab(matchedTab ? matchedTab.id : tabs[0].id);
    } catch {
      localStorage.removeItem(PORTAL_SESSION_KEY);
      sessionStorage.removeItem(PORTAL_SESSION_KEY);
      window.location.href = '/';
    }
  }, []);

  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem(PORTAL_SESSION_KEY);
    sessionStorage.removeItem(PORTAL_SESSION_KEY);
    window.location.href = '/';
  };

  // Auto-logout on session expiry (Firestore permission-denied detected anywhere in portal)
  useEffect(() => {
    const handler = () => {
      localStorage.setItem(SESSION_EXPIRED_KEY, '1');
      localStorage.removeItem(PORTAL_SESSION_KEY);
      sessionStorage.removeItem(PORTAL_SESSION_KEY);
      window.location.href = '/?login=1';
    };
    window.addEventListener('gohost:session-expired', handler);
    return () => window.removeEventListener('gohost:session-expired', handler);
  }, []);

  if (!user) return null;

  const tabs = buildNav(user);
  const renderPanel = PANELS[activeTab];
  const activeMeta = TAB_META[activeTab] || DEFAULT_META;
  const activeTab$ = tabs.find(t => t.id === activeTab);

  return (
    <>
    <div className="h-screen bg-neutral-50 flex flex-col overflow-hidden">

      {/* ── Top bar ── */}
      <header className="bg-white border-b border-neutral-200 px-6 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <img src={Logo} alt="GoHost" className="h-10 w-10 object-contain" />
          <span className="text-sm font-semibold text-neutral-500 tracking-wide uppercase">
            Staff Portal
          </span>
        </div>

        <div className="flex items-center gap-4">
          <div className="text-right hidden sm:block">
            <p className="text-sm font-semibold text-neutral-700 leading-tight">
              {user.firstName} {user.lastName}
            </p>
            {user.position && (
              <p className="text-xs text-neutral-400">{user.position}</p>
            )}
          </div>
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full capitalize ${ROLE_BADGE[user.role] || ROLE_BADGE.employee}`}>
            {user.role}
          </span>
          <button
            onClick={() => setShowLogoutConfirm(true)}
            className="text-xs px-3 py-1.5 rounded border border-neutral-300 text-neutral-500 hover:border-red-300 hover:text-red-600 transition-colors"
          >
            Logout
          </button>
        </div>
      </header>

      {/* ── Body ── */}
      <div className="flex flex-1 min-h-0">

        {/* Sidebar nav — desktop */}
        <nav className="hidden md:flex flex-col w-52 bg-white border-r border-neutral-200 py-6 px-3 shrink-0 overflow-y-auto">
          {tabs.map((tab) => {
            const meta = TAB_META[tab.id] || DEFAULT_META;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab.id)}
                className={`flex items-center gap-2.5 text-left px-3 py-2.5 rounded-lg text-sm font-medium mb-1 transition-colors ${
                  isActive ? meta.activeCls : meta.inactiveCls
                }`}
              >
                <FIcon name={meta.icon} size={15} />
                {tab.label}
              </button>
            );
          })}
        </nav>

        {/* Main content */}
        <main className="flex-1 overflow-y-auto p-6 md:p-8">

          {/* Tab bar — mobile */}
          <div className="flex gap-2 mb-6 overflow-x-auto md:hidden pb-1">
            {tabs.map((tab) => {
              const meta = TAB_META[tab.id] || DEFAULT_META;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabClick(tab.id)}
                  className={`shrink-0 flex items-center gap-1.5 text-sm px-4 py-2.5 rounded-full font-medium transition-colors ${
                    isActive ? meta.activeCls : `bg-neutral-100 ${meta.inactiveCls}`
                  }`}
                >
                  <FIcon name={meta.icon} size={14} />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Active panel */}
          <div>
            {activeTab !== 'tables' && activeTab$ && (
              <h2 className={`flex items-center gap-2.5 text-xl font-bold mb-6 ${activeMeta.headerCls}`}>
                <FIcon name={activeMeta.icon} size={20} />
                {activeTab$.label}
              </h2>
            )}
            {renderPanel ? renderPanel(user, handleDirtyChange) : <PlaceholderPanel title="Coming soon" />}
          </div>
        </main>
      </div>
    </div>

    {/* Unsaved changes guard */}
    {pendingTabId !== null && (
      <UnsavedChangesModal
        saving={guardSaving}
        onSave={handleGuardSave}
        onLeave={handleGuardLeave}
        onStay={handleGuardStay}
      />
    )}

    {/* Logout confirm */}
    {showLogoutConfirm && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4 py-6">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-xs flex flex-col overflow-hidden">
          <div className="px-6 pt-6 pb-4 text-center">
            <p className="text-base font-semibold text-neutral-800 mb-1">Log out?</p>
            <p className="text-sm text-neutral-500">You'll need to sign in again to access the portal.</p>
          </div>
          <div className="px-6 pb-6 flex flex-col gap-2">
            <button
              onClick={handleLogout}
              className="w-full py-2.5 text-sm font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Yes, log out
            </button>
            <button
              onClick={() => setShowLogoutConfirm(false)}
              className="w-full py-2.5 text-sm font-medium text-neutral-600 border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default PortalPage;
